/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { getApiBase } from '../utils/apiConfig';

const QuoteFlowContext = createContext(null);
const QUOTE_FLOW_STORAGE_KEY = 'movd.quoteFlow.pending';

export const useQuoteFlow = () => {
  const context = useContext(QuoteFlowContext);
  if (!context) {
    throw new Error('useQuoteFlow must be used within a QuoteFlowProvider');
  }
  return context;
};

const STEPS = [
  { id: 'service-details', label: 'Service Details', icon: '🚚' },
  { id: 'inventory', label: 'Inventory', icon: '📦' },
  { id: 'review', label: 'Review Move', icon: '✓' },
  { id: 'mover-selection', label: 'Choose Mover', icon: '👷' },
  { id: 'booking-details', label: 'Booking Details', icon: '📅' },
  { id: 'payment', label: 'Payment', icon: '💳' },
  { id: 'confirmation', label: 'Confirmation', icon: '✅' }
];

const getEstimateInputs = quoteData => ({
  origin: quoteData.serviceDetails.origin || '',
  destination: quoteData.serviceDetails.destination || '',
  moveDate: quoteData.serviceDetails.moveDate || null,
  timeWindow: quoteData.serviceDetails.timeWindow || '',
  serviceType: quoteData.serviceDetails.serviceType || 'moving',
  moveSize: quoteData.serviceDetails.moveSize || '',
  bedrooms: quoteData.estimate?.estimateDetails?.bedrooms || '',
  fullness: quoteData.estimate?.estimateDetails?.fullness || '',
  boxes: quoteData.estimate?.estimateDetails?.boxes ?? null,
});

const getEstimateFingerprint = quoteData => JSON.stringify(getEstimateInputs(quoteData));

const pricingFingerprint = data => JSON.stringify({
  serviceDetails: data.serviceDetails,
  inventory: data.inventory,
  accessDetails: data.bookingDetails?.accessDetails,
});

const fetchWithTimeout = async (url, options = {}, timeoutMs = 20000) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('The quote service took too long to respond. Please try again.');
    }
    throw error;
  } finally {
    clearTimeout(timer);
  }
};

export const QuoteFlowProvider = ({ children }) => {
  // Current step management
  const [currentStep, setCurrentStep] = useState(0);
  const [isQuoteActive, setIsQuoteActive] = useState(false);
  
  // Main quote data state
  const [quoteData, setQuoteData] = useState({
    serviceDetails: {
      serviceType: 'moving',
      origin: '',
      destination: '',
      moveDate: null,
      moveSize: '',
      distance: null,
      distanceKm: null,
      duration: null
    },
    estimate: {
      status: 'NOT_STARTED',
      quoteType: null,
      confidence: null,
      range: null,
      assumptions: [],
      error: '',
      inputFingerprint: null,
      estimateDetails: null,
    },
    locations: {
      origin: {
        address: '',
        city: '',
        province: '',
        postalCode: '',
        coordinates: null
      },
      destination: {
        address: '',
        city: '',
        province: '',
        postalCode: '',
        coordinates: null
      }
    },
    inventory: {
      rooms: {},
      items: [],
      specialItems: [],
      totalVolume: 0,
      additionalServices: []
    },
    selectedMover: null,
    quotes: [],
    bookingDetails: {
      preferredTime: '',
      contactInfo: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        alternateContact: ''
      },
      specialInstructions: '',
      accessDetails: {
        origin: {
          stairs: false,
          elevator: false,
          walkingDistance: 'short',
          parkingNotes: ''
        },
        destination: {
          stairs: false,
          elevator: false,
          walkingDistance: 'short',
          parkingNotes: ''
        }
      }
    },
    paymentInfo: {
      method: null,
      billing: {
        sameAsContact: true,
        address: '',
        city: '',
        province: '',
        postalCode: ''
      },
      deposit: null,
      total: null,
      completed: false,
      bookingId: null,
      jobNumber: null
    }
  });

  // Validation and error state
  const [errors, setErrors] = useState({});
  const [quoteError, setQuoteError] = useState('');
  const [touched, setTouched] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [quoteState, setQuoteState] = useState('DRAFT');
  const lastQuotedFingerprint = React.useRef(null);
  const estimateRequestGeneration = React.useRef(0);
  const latestQuoteData = React.useRef(quoteData);

  // Step completion tracking
  const [completedSteps, setCompletedSteps] = useState(new Set());

  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(QUOTE_FLOW_STORAGE_KEY);
      if (!saved) return;
      const parsed = JSON.parse(saved);
      if (parsed.quoteData) setQuoteData(previous => ({ ...previous, ...parsed.quoteData }));
      if (Number.isInteger(parsed.currentStep)) setCurrentStep(parsed.currentStep);
      if (typeof parsed.isQuoteActive === 'boolean') setIsQuoteActive(parsed.isQuoteActive);
      if (Array.isArray(parsed.completedSteps)) setCompletedSteps(new Set(parsed.completedSteps));
    } catch {
      sessionStorage.removeItem(QUOTE_FLOW_STORAGE_KEY);
    }
  }, []);

  useEffect(() => {
    try {
      sessionStorage.setItem(QUOTE_FLOW_STORAGE_KEY, JSON.stringify({
        quoteData,
        currentStep,
        isQuoteActive,
        completedSteps: [...completedSteps],
      }));
    } catch {
      // Session storage is only a continuity aid; the in-memory flow remains authoritative.
    }
  }, [quoteData, currentStep, isQuoteActive, completedSteps]);

  useEffect(() => {
    latestQuoteData.current = quoteData;
    if (lastQuotedFingerprint.current && pricingFingerprint(quoteData) !== lastQuotedFingerprint.current) {
      setQuoteState('DIRTY');
    }
    const estimate = quoteData.estimate;
    if (estimate?.status === 'READY' && estimate.inputFingerprint && getEstimateFingerprint(quoteData) !== estimate.inputFingerprint) {
      setQuoteData(previous => ({
        ...previous,
        estimate: { ...previous.estimate, status: 'DIRTY' },
      }));
    }
  }, [quoteData]);

  // Update quote data helper
  const updateQuoteData = useCallback((section, data) => {
    setQuoteData(prev => ({
      ...prev,
      [section]: section === 'quotes' || section === 'selectedMover' 
        ? data  // Direct assignment for arrays and single objects
        : {     // Object merge for nested objects
            ...prev[section],
            ...data
          }
    }));
  }, []);

  // Step navigation functions
  const nextStep = useCallback(() => {
    if (currentStep < STEPS.length - 1) {
      setCompletedSteps(prev => new Set([...prev, currentStep]));
      setCurrentStep(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentStep]);

  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentStep]);

  const goToStep = useCallback((stepIndex) => {
    if (stepIndex >= 0 && stepIndex < STEPS.length) {
      setCurrentStep(stepIndex);
    }
  }, []);

  // Validation functions for each step
  const validateStep = useCallback((stepIndex) => {
    const stepErrors = {};
    
    switch (stepIndex) {
      case 0: // Service Details
        if (!quoteData.serviceDetails.origin) {
          stepErrors.origin = 'Origin address is required';
        }
        if (!quoteData.serviceDetails.destination) {
          stepErrors.destination = 'Destination address is required';
        }
        if (!quoteData.serviceDetails.moveDate) {
          stepErrors.moveDate = 'Move date is required';
        }
        break;

      case 1: // Inventory — optional, user may skip
        break;

      case 3: // Mover Selection
        if (!quoteData.selectedMover) {
          stepErrors.mover = 'Please select a mover';
        }
        break;

      case 4: { // Booking Details
        const contact = quoteData.bookingDetails.contactInfo;
        if (!contact.firstName) stepErrors.firstName = 'First name is required';
        if (!contact.lastName) stepErrors.lastName = 'Last name is required';
        if (!contact.email) stepErrors.email = 'Email is required';
        if (!contact.phone) stepErrors.phone = 'Phone number is required';
        else if (!/^(\+1\s?)?(\d{3}|\(\d{3}\))[\s.-]?\d{3}[\s.-]?\d{4}$/.test(contact.phone.trim())) {
          stepErrors.phone = 'Enter a valid 10-digit phone number, for example (587) 343-3130';
        }
        break;
      }

      case 5: // Payment
        if (!quoteData.paymentInfo.method) {
          stepErrors.paymentMethod = 'Please select a payment method';
        }
        if (!quoteData.paymentInfo.completed) {
          stepErrors.payment = 'Please complete payment before continuing';
        }
        break;

      default:
        break;
    }

    return stepErrors;
  }, [quoteData]);

  // Check if current step is valid
  const isCurrentStepValid = useCallback(() => {
    const stepErrors = validateStep(currentStep);
    return Object.keys(stepErrors).length === 0;
  }, [currentStep, validateStep]);

  const calculateBrowserDistanceKm = useCallback((origin, destination) => {
    if (!origin || !destination || !window.google?.maps?.DistanceMatrixService) {
      return Promise.resolve(null);
    }

    return new Promise(resolve => {
      let settled = false;
      const finish = value => {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        resolve(value);
      };
      const timeout = setTimeout(() => finish(null), 5000);
      const service = new window.google.maps.DistanceMatrixService();
      service.getDistanceMatrix({
        origins: [origin],
        destinations: [destination],
        travelMode: window.google.maps.TravelMode.DRIVING,
        unitSystem: window.google.maps.UnitSystem.METRIC,
      }, (response, status) => {
        const element = response?.rows?.[0]?.elements?.[0];
        if (status === 'OK' && element?.status === 'OK' && element.distance?.value) {
          finish(element.distance.value / 1000);
        } else {
          finish(null);
        }
      });
    });
  }, []);

  const calculateEstimate = useCallback(async (estimateDetails) => {
    const requestGeneration = ++estimateRequestGeneration.current;
    const currentQuoteData = latestQuoteData.current;
    const requestInputs = {
      ...currentQuoteData,
      estimate: { ...currentQuoteData.estimate, estimateDetails },
    };
    const inputFingerprint = getEstimateFingerprint(requestInputs);
    updateQuoteData('estimate', { status: 'LOADING', error: '', estimateDetails, inputFingerprint });
    try {
      const distanceKm = currentQuoteData.serviceDetails.distanceKm || await calculateBrowserDistanceKm(
        currentQuoteData.serviceDetails.origin,
        currentQuoteData.serviceDetails.destination
      );
      if (!Number.isFinite(Number(distanceKm))) {
        throw new Error('Please check your addresses so we can calculate the route.');
      }
      const response = await fetch(`${getApiBase()}/estimates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          origin: currentQuoteData.serviceDetails.origin,
          destination: currentQuoteData.serviceDetails.destination,
          moveDate: currentQuoteData.serviceDetails.moveDate,
          arrivalWindow: currentQuoteData.serviceDetails.timeWindow,
          serviceType: currentQuoteData.serviceDetails.serviceType,
          distanceKm,
          ...estimateDetails,
        }),
      });
      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.range || data.quoteType !== 'ESTIMATE' || data.bookable !== false) {
        throw new Error(data?.error || 'We could not calculate your estimate right now.');
      }
      if (requestGeneration !== estimateRequestGeneration.current || inputFingerprint !== getEstimateFingerprint(latestQuoteData.current)) {
        return null;
      }
      updateQuoteData('serviceDetails', { distanceKm });
      updateQuoteData('estimate', { ...data, status: 'READY', error: '', estimateDetails, inputFingerprint });
      return data;
    } catch (error) {
      if (requestGeneration === estimateRequestGeneration.current) {
        updateQuoteData('estimate', { status: 'ERROR', error: error.message || 'We could not calculate your estimate right now.' });
      }
      return null;
    }
  }, [calculateBrowserDistanceKm, updateQuoteData]);

  // Get quote estimates from backend
  const getQuoteEstimate = useCallback(async () => {
    setIsLoading(true);
    setQuoteError('');
    try {
      const { serviceDetails, inventory, bookingDetails, estimate } = quoteData;
      const inventoryData = inventory && typeof inventory === 'object' ? inventory : {};
      const params = new URLSearchParams();
      if (serviceDetails.moveDate) params.set('moveDate', serviceDetails.moveDate);
      if (serviceDetails.origin) params.set('origin', serviceDetails.origin);
      if (serviceDetails.destination) params.set('destination', serviceDetails.destination);
      params.set('serviceType', serviceDetails.serviceType || 'moving');
      if (inventoryData.additionalServices?.includes('packing')) params.set('packingRequired', 'true');

      const response = await fetchWithTimeout(`${getApiBase()}/movers${params.toString() ? `?${params}` : ''}`);
      if (!response.ok) {
        throw new Error('Failed to fetch movers');
      }
      const movers = await response.json();
      const moversArray = Array.isArray(movers) ? movers : [];

      if (moversArray.length === 0) {
        const diagnosticParams = new URLSearchParams(params);
        diagnosticParams.set('diagnostics', 'true');
        const diagnosticResponse = await fetchWithTimeout(`${getApiBase()}/movers?${diagnosticParams}`);
        const diagnosticData = diagnosticResponse.ok ? await diagnosticResponse.json().catch(() => null) : null;
        const exclusionCodes = [...new Set((diagnosticData?.exclusions || []).flatMap(item => item.codes || []))];
        const messages = {
          OUTSIDE_SERVICE_AREA: 'No approved movers currently cover both locations.',
          TIME_UNAVAILABLE: 'No movers are available on the selected date or time.',
          BOOKING_CONFLICT: 'All matching movers are already booked for that time.',
          LONG_DISTANCE_NOT_SUPPORTED: 'No matching movers support this route length.',
          SERVICE_AREA_NOT_CONFIGURED: 'Matching movers have not configured their service areas yet.',
          PRICING_INCOMPLETE: 'Matching movers are still completing their pricing setup.',
          SERVICE_NOT_SUPPORTED: 'No matching movers support this service.',
        };
        const diagnosticMessage = exclusionCodes.map(code => messages[code]).filter(Boolean)[0];
        if (diagnosticMessage) throw new Error(diagnosticMessage);
        if (!diagnosticData) throw new Error('We could not check mover availability right now. Please try again.');
        throw new Error('No eligible movers are available for these move details. Try another date or route.');
      }

      let distanceKm = serviceDetails.distanceKm || await calculateBrowserDistanceKm(serviceDetails.origin, serviceDetails.destination);
      if (!Number.isFinite(Number(distanceKm))) {
        const distanceResponse = await fetchWithTimeout(`${getApiBase()}/route-distance`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ origin: serviceDetails.origin, destination: serviceDetails.destination }),
        });
        const distanceData = await distanceResponse.json().catch(() => ({}));
        if (distanceResponse.ok && Number.isFinite(Number(distanceData.distanceKm))) {
          distanceKm = Number(distanceData.distanceKm);
        }
      }
      if (distanceKm) {
        updateQuoteData('serviceDetails', { distanceKm });
      }

      const quoteInventory = (Array.isArray(inventoryData.items) ? inventoryData.items : []).map(item => ({
        id: item.id || item._id,
        name: item.name,
        qty: item.quantity || item.qty || 1,
        volume: item.volume,
        weight: item.weight,
      }));

      const quotes = (await Promise.all(moversArray.map(async (mover) => {
        try {
          const quoteResponse = await fetchWithTimeout(`${getApiBase()}/ai-quote`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              origin: serviceDetails.origin,
              destination: serviceDetails.destination,
              moverId: mover._id,
              moveDate: serviceDetails.moveDate,
              timeWindow: serviceDetails.timeWindow,
              serviceType: serviceDetails.serviceType || 'moving',
              distanceKm,
              inventory: quoteInventory,
              specialItems: Array.isArray(inventoryData.specialItems) ? inventoryData.specialItems : [],
              additionalServices: Array.isArray(inventoryData.additionalServices) ? inventoryData.additionalServices : [],
              estimateDetails: estimate?.estimateDetails || inventoryData.estimateDetails,
              supersedesQuoteId: quoteData.quotes.find(previous => String(previous.id) === String(mover._id))?.quoteId,
              originStairs: bookingDetails.accessDetails?.origin?.stairs || false,
              originLongCarry: bookingDetails.accessDetails?.origin?.walkingDistance === 'long',
              destinationStairs: bookingDetails.accessDetails?.destination?.stairs || false,
              destinationLongCarry: bookingDetails.accessDetails?.destination?.walkingDistance === 'long',
            }),
          });
          if (!quoteResponse.ok) {
            const errorData = await quoteResponse.json().catch(() => ({}));
            throw new Error(errorData.error || 'Unable to calculate quote');
          }
          const quote = await quoteResponse.json();
          const hours = quote.breakdown?.laborCost?.estimatedHours;
          return {
            id: mover._id,
            company: mover.name,
            rating: mover.rating || 0,
            reviews: mover.totalReviews || mover.reviews || 0,
            price: quote.price,
            quoteId: quote.quoteId,
            duration: hours ? `${hours} hour${hours === 1 ? '' : 's'}` : 'Duration TBD',
            services: mover.services || [],
            insurance: mover.insurance ? 'Insurance on file' : 'MOVD verified',
            availability: 'Available',
            phone: mover.phone,
            email: mover.email,
            address: mover.address,
            description: mover.description || 'Professional moving services',
            breakdown: quote.breakdown,
            pricingSnapshot: quote.pricingSnapshot,
            pricingEngineVersion: quote.pricingEngineVersion,
            riskAssessment: quote.riskAssessment,
            quoteProtectionStatus: quote.quoteProtectionStatus,
            requiresMoverVerification: quote.requiresMoverVerification,
            currency: quote.currency || 'CAD',
            quoteType: quote.quoteType,
            bookable: quote.bookable,
            bookabilityReasons: quote.reasons || [],
            googleBusiness: mover.googleBusiness || null,
          };
        } catch (error) {
          return { error: error.message || 'Unable to calculate quote', moverId: mover._id };
        }
      })));

      const failedQuotes = quotes.filter(quote => quote?.error);
      const validQuotes = quotes.filter(quote => quote && !quote.error);
      if (validQuotes.length === 0 && failedQuotes.length > 0) {
        setQuoteError(failedQuotes[0].error);
      }
      
      let rankedQuotes = validQuotes;
      if (validQuotes.length > 0) {
        try {
          const rankingResponse = await fetchWithTimeout(`${getApiBase()}/mover-matches`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              quotes: validQuotes,
              moveRequest: {
                origin: serviceDetails.origin,
                destination: serviceDetails.destination,
                moveDate: serviceDetails.moveDate,
                distanceKm,
                serviceType: serviceDetails.serviceType || 'moving',
                packingRequired: bookingDetails.accessDetails?.packingRequired || false,
              },
            }),
          });
          if (rankingResponse.ok) {
            const rankingData = await rankingResponse.json();
            const quoteByMover = new Map(validQuotes.map(quote => [String(quote.id), quote]));
            rankedQuotes = (rankingData.ranked || []).map(result => ({
              ...(result.quote || quoteByMover.get(String(result.moverId)) || {}),
              matchScore: result.matchScore,
              matchRank: result.rank,
              matchLabel: result.label,
              matchReasons: result.reasons,
              matchScoreBreakdown: result.scoreBreakdown,
              matchAlgorithmVersion: rankingData.algorithmVersion,
            }));
          }
        } catch (rankingError) {
          console.warn('Mover ranking unavailable; preserving quote order.', rankingError);
        }
      }

      updateQuoteData('quotes', rankedQuotes);
      const currentMover = quoteData.selectedMover;
      if (currentMover) {
        const refreshedMover = rankedQuotes.find(quote => String(quote.id) === String(currentMover.id));
        updateQuoteData('selectedMover', refreshedMover || null);
      }
      lastQuotedFingerprint.current = pricingFingerprint({
        ...quoteData,
        serviceDetails: distanceKm ? { ...quoteData.serviceDetails, distanceKm } : quoteData.serviceDetails,
      });
      setQuoteState('READY');
    } catch (error) {
      console.error('Error fetching movers:', error);
      setQuoteError(error.message || 'Unable to fetch movers');
      updateQuoteData('quotes', []);
    } finally {
      setIsLoading(false);
    }
  }, [quoteData, updateQuoteData, calculateBrowserDistanceKm]);

  // Reset quote flow
  const resetQuoteFlow = useCallback(() => {
    setCurrentStep(0);
    setQuoteData({
      serviceDetails: {
        serviceType: 'moving',
        origin: '',
        destination: '',
        moveDate: null,
        moveSize: '',
        distance: null,
        distanceKm: null,
        duration: null
      },
      estimate: {
        status: 'NOT_STARTED',
        quoteType: null,
        confidence: null,
        range: null,
        assumptions: [],
        error: '',
        inputFingerprint: null,
        estimateDetails: null,
      },
      locations: {
        origin: {
          address: '',
          city: '',
          province: '',
          postalCode: '',
          coordinates: null
        },
        destination: {
          address: '',
          city: '',
          province: '',
          postalCode: '',
          coordinates: null
        }
      },
      inventory: {
        rooms: {},
        items: [],
        specialItems: [],
        totalVolume: 0,
        additionalServices: []
      },
      selectedMover: null,
      quotes: [],
      bookingDetails: {
        preferredTime: '',
        contactInfo: {
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          alternateContact: ''
        },
        specialInstructions: '',
        accessDetails: {
          origin: {
            stairs: false,
            elevator: false,
            walkingDistance: 'short',
            parkingNotes: ''
          },
          destination: {
            stairs: false,
            elevator: false,
            walkingDistance: 'short',
            parkingNotes: ''
          }
        }
      },
      paymentInfo: {
        method: null,
        billing: {
          sameAsContact: true,
          address: '',
          city: '',
          province: '',
          postalCode: ''
        },
        deposit: null,
        total: null
      }
    });
    setErrors({});
    setQuoteError('');
    setQuoteState('DRAFT');
    lastQuotedFingerprint.current = null;
    setTouched({});
    setCompletedSteps(new Set());
    sessionStorage.removeItem(QUOTE_FLOW_STORAGE_KEY);
  }, []);

  // Calculate progress percentage
  const progressPercentage = ((currentStep + 1) / STEPS.length) * 100;

  const value = {
    // State
    currentStep,
    isQuoteActive,
    quoteData,
    errors,
    quoteError,
    calculateEstimate,
    touched,
    isLoading,
    completedSteps,
    progressPercentage,
    quoteState,
    
    // Step management
    nextStep,
    prevStep,
    goToStep,
    isCurrentStepValid,
    
    // Data management
    updateQuoteData,
    getQuoteEstimate,
    resetQuoteFlow,
    validateStep,
    setIsLoading,
    setErrors,
    setTouched,
    setIsQuoteActive,
    
    // Constants
    STEPS
  };

  return (
    <QuoteFlowContext.Provider value={value}>
      {children}
    </QuoteFlowContext.Provider>
  );
};

export default QuoteFlowProvider;